import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { sha256 } from 'js-sha256';

/**
 * Hook personnalisé pour écouter les scans NFC globaux
 * Gère automatiquement les scans NFC effectués en dehors de l'application
 */
const useNFCListener = (user) => {
  const navigate = useNavigate();
  const location = useLocation();
  const abortControllerRef = useRef(null);
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    // Ne pas activer le listener si on est déjà sur une page de gestion NFC
    const isNFCPage = location.pathname.includes('/nfc') ||
                      location.pathname.includes('/appointment');

    if (isNFCPage || !('NDEFReader' in window)) {
      return;
    }

    const setupNFCListener = async () => {
      try {
        abortControllerRef.current = new AbortController();
        const ndef = new window.NDEFReader();

        console.log('🎧 Activation du listener NFC global');

        await ndef.scan({
          signal: abortControllerRef.current.signal
        });

        ndef.addEventListener("reading", async (event) => {
          // Empêcher le traitement multiple
          if (hasProcessedRef.current) {
            console.log('⏭️ Scan NFC déjà traité, ignoré');
            return;
          }

          hasProcessedRef.current = true;
          console.log('📱 Tag NFC détecté depuis l\'extérieur de l\'application');

          try {
            const { message, serialNumber } = event;
            console.log('Serial number:', serialNumber);

            let nfcData = null;
            let urlData = null;

            // Lire les records du message NFC
            for (const record of message.records) {
              const textDecoder = new TextDecoder(record.encoding || 'utf-8');
              const decodedData = textDecoder.decode(record.data);

              if (record.recordType === "text") {
                try {
                  nfcData = JSON.parse(decodedData);
                  console.log('✅ Données JSON trouvées:', nfcData);
                } catch (error) {
                  console.error('❌ Erreur parsing JSON:', error);
                }
              } else if (record.recordType === "url") {
                urlData = decodedData;
                console.log('🔗 URL trouvée:', urlData);
              }
            }

            if (!nfcData) {
              console.error('❌ Aucune donnée NFC valide trouvée');
              alert('Tag NFC invalide. Veuillez le reprogrammer.');
              hasProcessedRef.current = false;
              return;
            }

            // Vérifier les données NFC
            const { isValid, error, clientData } = verifyNfcData(nfcData);

            if (!isValid) {
              console.error('❌ Vérification NFC échouée:', error);
              alert(error);
              hasProcessedRef.current = false;
              return;
            }

            console.log('✅ Tag NFC valide pour le client:', clientData.clientId);

            // Si l'utilisateur est connecté, rediriger vers le dashboard avec les infos de pointage
            if (user) {
              console.log('👤 Utilisateur connecté, redirection vers le dashboard');
              navigate('/dashboard', {
                state: {
                  nfcScan: true,
                  nfcData: clientData,
                  timestamp: new Date().toISOString()
                }
              });
            } else {
              // Si pas connecté, stocker les données et rediriger vers login
              console.log('🔒 Utilisateur non connecté, stockage des données');
              sessionStorage.setItem('pendingNFCScan', JSON.stringify({
                nfcData: clientData,
                timestamp: new Date().toISOString()
              }));
              navigate('/login', {
                state: {
                  message: 'Veuillez vous connecter pour effectuer le pointage NFC'
                }
              });
            }

          } catch (error) {
            console.error('❌ Erreur lors du traitement du tag NFC:', error);
            alert('Erreur lors de la lecture du tag NFC: ' + error.message);
          } finally {
            // Réinitialiser après un délai pour permettre de nouveaux scans
            setTimeout(() => {
              hasProcessedRef.current = false;
            }, 3000);
          }
        });

      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('❌ Erreur lors de l\'initialisation du listener NFC:', error);
        }
      }
    };

    // Démarrer le listener NFC
    setupNFCListener();

    // Nettoyer à la fin
    return () => {
      if (abortControllerRef.current) {
        console.log('🛑 Arrêt du listener NFC global');
        abortControllerRef.current.abort();
      }
    };
  }, [user, navigate, location.pathname]);
};

/**
 * Vérifie la validité des données NFC
 */
const verifyNfcData = (nfcData) => {
  try {
    const { cid, clientId, hash, addr, exp, serialNumber } = nfcData;

    // Vérifier que toutes les données requises sont présentes
    if (!cid || !clientId || !hash || !exp) {
      return {
        isValid: false,
        error: 'Données NFC incomplètes. Veuillez reprogrammer le tag.'
      };
    }

    // Vérifier l'expiration
    const currentDate = new Date();
    const expDate = new Date(exp);

    if (currentDate > expDate) {
      return {
        isValid: false,
        error: 'Le tag NFC a expiré. Veuillez le reprogrammer.'
      };
    }

    // Vérifier le hash
    const secretKey = "Son_of_the_Bitch!"; // Même clé que dans NFCModal
    const expectedHash = sha256(cid + secretKey + clientId).slice(0, 20);

    if (hash !== expectedHash) {
      return {
        isValid: false,
        error: 'Tag NFC invalide. Le hash ne correspond pas.'
      };
    }

    // Décoder l'adresse si elle existe
    let decodedAddress = '';
    if (addr) {
      try {
        decodedAddress = atob(addr);
      } catch (error) {
        console.warn('Impossible de décoder l\'adresse:', error);
      }
    }

    return {
      isValid: true,
      clientData: {
        clientId,
        cid,
        serialNumber,
        address: decodedAddress,
        exp
      }
    };

  } catch (error) {
    console.error('Erreur lors de la vérification des données NFC:', error);
    return {
      isValid: false,
      error: 'Erreur lors de la vérification du tag NFC.'
    };
  }
};

export default useNFCListener;
